import { HexOklch } from '../HexOklch/HexOklch';
import { PaletteEngine } from '../PaletteEngine/PaletteEngine';
import { SemanticMapper } from '../SemanticMapper/SemanticMapper';
import type { Theme, ThemeDeclaration } from '../Theme';

interface IThemeBuilder {
  buildAll(decls: readonly ThemeDeclaration[]): readonly Theme[];
  buildTheme(decl: ThemeDeclaration): Theme;
  defineTheme(decl: ThemeDeclaration): ThemeDeclaration;
}

const _titleize = (kebab: string): string =>
  kebab
    .split('-')
    .filter(s => s.length > 0)
    .map(s => s.charAt(0).toUpperCase() + s.slice(1))
    .join(' ');

export const ThemeBuilder: IThemeBuilder = {
  /**
   * Resolve every declaration in `decls` into a fully realised `Theme`.
   * @param decls Declarations to resolve, in order.
   * @returns Resolved themes in the same order.
   */
  buildAll(decls: readonly ThemeDeclaration[]): readonly Theme[] {
    return decls.map(decl => ThemeBuilder.buildTheme(decl));
  },
  /**
   * Resolve a `ThemeDeclaration` into a fully realised `Theme` (foundation +
   * semantic per mode + radius preset).
   * @param decl Theme declaration.
   * @returns Fully resolved Theme.
   */
  buildTheme(decl: ThemeDeclaration): Theme {
    const normalized = ThemeBuilder.defineTheme(decl);
    const foundation = PaletteEngine.derive(normalized);
    const semantic = SemanticMapper.map(foundation, normalized);
    return {
      defaultMode: normalized.defaultMode,
      displayName: normalized.displayName ?? normalized.name,
      foundation,
      name: normalized.name,
      radius: normalized.radius,
      semantic
    };
  },
  /**
   * Validate and normalize a theme declaration.
   * Pure: no side effects, no global state. Duplicate-name detection is the
   * Vite plugin's responsibility (Phase 4.1).
   * @param decl User-supplied declaration.
   * @returns Normalized declaration with `displayName` filled in.
   */
  defineTheme(decl: ThemeDeclaration): ThemeDeclaration {
    if (typeof decl.name !== 'string' || decl.name.length === 0) throw new Error('defineTheme: `name` is required');
    if (!HexOklch.isValidHex(decl.brand)) throw new Error(`defineTheme: invalid brand hex "${decl.brand}"`);
    if (!HexOklch.isValidHex(decl.accent)) throw new Error(`defineTheme: invalid accent hex "${decl.accent}"`);
    return {
      ...decl,
      displayName: decl.displayName ?? _titleize(decl.name)
    };
  }
};
